import Image from 'next/image';
import NextLink from 'next/link';

import { type BoxProps, Box, Button, Flex, Heading, LinkBox, LinkOverlay, Text } from '@chakra-ui/react';

import { type FlashSaleEyebrowIcon, FlashSaleEyebrow } from './flash-sale-eyebrow';

export type FlashSaleBannerProps = {
  backgroundGradient: string;
  backgroundImageBlendMode?: BoxProps['mixBlendMode'];
  backgroundImageDesktop: string;
  backgroundImageMobile: string;
  backgroundImageObjectPosition?: string;
  backgroundImageOpacity?: number;
  borderColor: string;
  checkoutHref: string;
  contentMaxWidth?: string;
  ctaColor: string;
  ctaLabel: string;
  eyebrowColor: string;
  eyebrowIcon?: FlashSaleEyebrowIcon;
  eyebrowLabel?: string;
  headline: string;
  hideEyebrow?: boolean;
  imagePriority?: boolean;
  subtitle: string;
  subtitleColor: string;
  onCtaClick?: () => void;
};

export function FlashSaleBanner({
  backgroundGradient,
  backgroundImageBlendMode,
  backgroundImageDesktop,
  backgroundImageMobile,
  backgroundImageObjectPosition,
  backgroundImageOpacity,
  borderColor,
  checkoutHref,
  contentMaxWidth,
  ctaColor,
  ctaLabel,
  eyebrowColor,
  eyebrowIcon,
  eyebrowLabel,
  headline,
  hideEyebrow,
  imagePriority,
  subtitle,
  subtitleColor,
  onCtaClick,
}: FlashSaleBannerProps) {
  return (
    <LinkBox
      bgGradient={backgroundGradient}
      border='1px solid'
      borderColor={borderColor}
      borderRadius='8px'
      height='100%'
      isolation='isolate'
      minHeight={{ base: 'auto', md: '120px' }}
      overflow='hidden'
      position='relative'
      width='100%'
    >
      <ResponsiveBackground
        blendMode={backgroundImageBlendMode}
        desktopSrc={backgroundImageDesktop}
        imagePriority={imagePriority}
        mobileSrc={backgroundImageMobile}
        objectPosition={backgroundImageObjectPosition}
        opacity={backgroundImageOpacity}
      />
      <Flex
        alignItems={{ base: 'flex-start', md: 'center' }}
        flexDirection={{ base: 'column', md: 'row' }}
        gap={{ base: 3, md: 4 }}
        height='100%'
        justifyContent='space-between'
        minHeight={{ base: 'auto', md: '120px' }}
        padding={{ base: '20px', md: '32px' }}
        position='relative'
        zIndex={1}
      >
        <Box maxWidth={{ md: contentMaxWidth ?? '430px' }} minWidth={0}>
          {hideEyebrow ? null : (
            <Box marginBottom={{ base: 1.5, md: 2 }}>
              <FlashSaleEyebrow color={eyebrowColor} icon={eyebrowIcon} label={eyebrowLabel} size='compact' />
            </Box>
          )}
          <Heading
            as='h3'
            color='white'
            fontSize={{ base: '1.25rem', md: '1.5rem' }}
            fontWeight='600'
            letterSpacing='-0.01em'
            lineHeight={1.15}
            whiteSpace='pre-wrap'
          >
            {headline}
          </Heading>
          <Text color={subtitleColor} fontSize={{ base: '13px', md: 'md' }} lineHeight={1.5}>
            {subtitle}
          </Text>
        </Box>
        <Button
          as='span'
          backgroundColor={ctaColor}
          color='white'
          colorScheme='blue'
          flexShrink={0}
          pointerEvents='none'
          size='md'
          tabIndex={-1}
          variant='solid'
          width='fit-content'
          aria-hidden
        >
          {ctaLabel}
        </Button>
      </Flex>
      <LinkOverlay
        aria-label={`${headline}: ${ctaLabel}`}
        as={NextLink}
        href={checkoutHref}
        inset={0}
        position='absolute'
        zIndex={2}
        onClick={onCtaClick}
      />
    </LinkBox>
  );
}

function ResponsiveBackground({
  blendMode,
  desktopSrc,
  mobileSrc,
  imagePriority,
  objectPosition,
  opacity,
}: {
  blendMode?: BoxProps['mixBlendMode'];
  desktopSrc: string;
  mobileSrc: string;
  imagePriority?: boolean;
  objectPosition?: string;
  opacity?: number;
}) {
  return (
    <Box
      bottom={0}
      left={0}
      mixBlendMode={blendMode}
      opacity={opacity}
      pointerEvents='none'
      position='absolute'
      right={0}
      top={0}
    >
      <Box display={{ base: 'block', md: 'none' }} height='100%' position='relative' width='100%'>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes='100vw'
          src={mobileSrc}
          style={{ objectFit: 'cover', objectPosition: objectPosition ?? 'right center' }}
          fill
        />
      </Box>
      <Box display={{ base: 'none', md: 'block' }} height='100%' position='relative' width='100%'>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes='656px'
          src={desktopSrc}
          style={{ objectFit: 'cover', objectPosition: objectPosition ?? 'right center' }}
          fill
        />
      </Box>
    </Box>
  );
}
